import { useTranslation } from "react-i18next";
import { Search } from "@/components/ui/combobox-search";
import { InputWithAlert } from "@/components/ui/input-with-alert";
import { Switch } from "@/components/ui/switch";
import { getContractors } from "@/service/weexpertService";
import { GET_COUNTRY_NAME_BY_CODE, COUNTRY_TYPE_MAP } from "@/constants";

export const RecipientSection = ({ form, inputChecked = false }) => {
  const { t } = useTranslation();
  const empty_error = t('empty_data_invalid');

  const {
    isViewMode,
    client,
    useDifferentRecipient,
    setUseDifferentRecipient,
    recipientCounterpartyName,
    recipientCounterpartyOrigin,
    recipientCounterpartyNip,
    recipientCounterpartyEmail,
    recipientCounterpartyType,
    recipientCounterpartyAddress,
    recipientCounterpartyPostIndex,
    recipientCounterpartyCity,
    recipientCounterpartyCountry,
    handleRecipientSelect,
    canChangeCounterpartyOrRecipient,
    invoice,
  } = form;

  // View mode - only show if recipient exists
  if (isViewMode) {
    if (!invoice?.recipientId) return null;

    return (
      <div className='w-full'>
        <div className="w-full text-xl font-medium py-6">
          {t('different_recipient')}
        </div>
        <div className='w-full md:w-9/12'>
          <div className='w-full flex justify-between flex-wrap'>
            <div className='w-full md:w-[49%] m-auto md:m-0'>
              <InputWithAlert
                label={t("common:counterparty")}
                value={invoice?.recipient?.name ?? ''}
                readOnly={true}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              <InputWithAlert
                label={t("common:origin")}
                value={invoice ? COUNTRY_TYPE_MAP(t)[invoice.recipient?.origin] ?? '' : ''}
                readOnly={true}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              <InputWithAlert
                label={t("invoices:positions_name")}
                value={invoice?.recipient?.name ?? ''}
                readOnly={true}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              <InputWithAlert
                label='NIP'
                value={invoice?.recipient?.NIP ?? ''}
                readOnly={true}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              <InputWithAlert
                label='Email'
                value={invoice?.recipient?.email ?? 'N/A'}
                readOnly={true}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
            </div>
            <div className='w-full md:w-[49%] m-auto md:m-0'>
              <InputWithAlert
                label={t("common:type")}
                value={invoice?.recipient?.counterpartyType ?? ''}
                readOnly={true}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              <InputWithAlert
                label={t("common:address")}
                value={invoice?.recipient?.address ?? ''}
                readOnly={true}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              <InputWithAlert
                label={t("common:post_index")}
                value={invoice?.recipient?.postIndex ?? ''}
                readOnly={true}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              <InputWithAlert
                label={t("common:city")}
                value={invoice?.recipient?.city ?? ''}
                readOnly={true}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              <InputWithAlert
                label={t("common:country")}
                value={invoice ? GET_COUNTRY_NAME_BY_CODE(invoice.recipient?.country) : ''}
                readOnly={true}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Create/Edit mode
  return (
    <div className='w-full'>
      <div className="w-full text-xl font-medium py-6">
        {t('different_recipient')}
        <Switch
          className={"ml-3"}
          checked={useDifferentRecipient}
          onCheckedChange={setUseDifferentRecipient}
        />
      </div>
      {useDifferentRecipient && (
        <div className='w-full'>
          <div className='w-full flex justify-between flex-wrap'>
            <div className='w-full md:w-[49%] m-auto md:m-0'>
              {canChangeCounterpartyOrRecipient ? (
                <Search
                  className={'pt-[10px]'}
                  queryKey="counterparties"
                  fetchOption={(query) => {
                    if (!client || !client.id) return [];
                    return getContractors(100, 0, query, [{ field: 'client', value: [client.id] }])
                      .then((res) => {
                        return res.data.counterparties.map(counterparty => ({
                          label: counterparty.name,
                          value: counterparty.id,
                          counterparty
                        }));
                      });
                  }}
                  placeholder={t("common:counterparty") + "*"}
                  label={t("common:counterparty") + "*"}
                  value={recipientCounterpartyName}
                  error={inputChecked && useDifferentRecipient && !recipientCounterpartyName}
                  errorMessage={empty_error}
                  onChange={(e) => handleRecipientSelect(e.counterparty)}
                />
              ) : (
                <InputWithAlert
                  placeholder={t("phone")}
                  value={recipientCounterpartyName ?? ''}
                  label={t("common:counterparty")}
                  onChange={() => {}}
                  disabled
                  inputClassName={'h-12 mt-2 mb-4 bg-white'}
                />
              )}
              <InputWithAlert
                placeholder={t("counterparties:origin")}
                value={recipientCounterpartyOrigin ?? ''}
                label={t("counterparties:origin")}
                onChange={() => {}}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              {recipientCounterpartyType === 'COMPANY' && (
                <InputWithAlert
                  placeholder={"NIP"}
                  label="NIP"
                  value={recipientCounterpartyNip ?? ''}
                  onChange={() => {}}
                  disabled
                  inputClassName={'h-12 mt-2 mb-4 bg-white'}
                />
              )}
              <InputWithAlert
                placeholder={"Email"}
                label="Email"
                value={recipientCounterpartyEmail ?? ''}
                onChange={() => {}}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
            </div>
            <div className='w-full md:w-[49%] m-auto md:m-0'>
              <InputWithAlert
                placeholder={t("common:type")}
                label={t("common:type")}
                value={recipientCounterpartyType ?? ''}
                onChange={() => {}}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              <InputWithAlert
                placeholder={t("common:address")}
                label={t("common:address")}
                value={recipientCounterpartyAddress ?? ''}
                onChange={() => {}}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              <InputWithAlert
                placeholder={t("common:post_index")}
                label={t("common:post_index")}
                value={recipientCounterpartyPostIndex ?? ''}
                onChange={() => {}}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              <InputWithAlert
                placeholder={t("common:city")}
                label={t("common:city")}
                value={recipientCounterpartyCity ?? ''}
                onChange={() => {}}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              <InputWithAlert
                placeholder={t("common:country")}
                label={t("common:country")}
                value={GET_COUNTRY_NAME_BY_CODE(recipientCounterpartyCountry)}
                onChange={() => {}}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
